import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { injectScript } from 'client/utils/inject-script';
import { logger } from 'client/utils/logger/logger';

import './google-preferred-source.scss';

const PREFERRED_SOURCE_BADGE = getStaticImageUrl('/img/google/preferred-source-light.svg');
const GOOGLE_PREFERRED_SOURCE_SCRIPT_ID = 'google-preferred-source-script';
const GOOGLE_PREFERRED_SOURCE_SCRIPT_SRC = 'https://news.google.com/swg/js/v1/publisher.js';

export function GooglePreferredSource({ className = null }) {
  const preferredSourceRef = useRef(null);

  const handleClick = () => {
    preferredSourceRef.current?.addPreferredSource();
  };

  useEffect(() => {
    let isMounted = true;

    const initializePreferredSource = preferredSource => {
      if (!isMounted) {
        return;
      }

      preferredSource.init({
        theme: 'light',
        lang: 'en',
      });
      preferredSourceRef.current = preferredSource;
    };

    window.PREFERRED_SOURCE = window.PREFERRED_SOURCE || [];
    window.PREFERRED_SOURCE.push(initializePreferredSource);

    if (!document.getElementById(GOOGLE_PREFERRED_SOURCE_SCRIPT_ID)) {
      injectScript(GOOGLE_PREFERRED_SOURCE_SCRIPT_SRC, {}, GOOGLE_PREFERRED_SOURCE_SCRIPT_ID, {
        'preferred-sources-control': 'manual',
      }).catch(() => logger.warn('Google Preferred Source script injection failed'));
    }

    return () => {
      isMounted = false;
      preferredSourceRef.current = null;
    };
  }, []);

  return (
    <section
      className={classnames(
        'google-preferred-source d-flex flex-column align-items-center justify-content-center gap-1 px-1 py-0_75 rounded-8 bg-white',
        className
      )}
      aria-label="Use Edmunds as your trusted source"
      data-tracking-parent="google_preferred_partner"
    >
      <p className="d-none d-md-block size-16 fw-bold w-100 mb-0">Use Edmunds as Your Trusted Source</p>
      <div className="d-flex flex-column flex-md-row align-items-center gap-1 gap-md-1_5 w-100">
        <button
          type="button"
          onClick={handleClick}
          className="button btn flex-shrink-0 p-0 border-0 rounded-8"
          aria-label="Add as a preferred source on Google"
          data-tracking-id="google_preferred_partner"
          data-tracking-ignore-value=""
        >
          <img
            src={PREFERRED_SOURCE_BADGE}
            alt=""
            aria-hidden="true"
            className="d-block w-100 h-100"
            width={182}
            height={57}
          />
        </button>
        <p className="flex-grow-1 min-w-0 text-center text-md-start size-16 mb-0">
          Make <span className="fw-bold">Edmunds</span> your favorite search result for{' '}
          <span className="fw-bold">car reviews</span>, <span className="fw-bold">prices paid</span>,{' '}
          <span className="fw-bold">appraisals</span> and <span className="fw-bold">inventory</span>.
        </p>
      </div>
    </section>
  );
}

GooglePreferredSource.propTypes = {
  className: PropTypes.string,
};
